import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import logger from '@/lib/logging';
import { logRoutingDecision } from '@/lib/routing-debug';
import { isStaffProspectPath, staffProspectLoginHref } from '@/lib/sales-prospect-ghl-attach';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute ensures users are authenticated before accessing protected pages.
 * Now checks server-side session for authentication status.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading, user, serverUser } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    logger.debug('[ProtectedRoute] Loading state:', { loading, path: location.pathname });
    logRoutingDecision('loading', {
      requestContext: {
        path: location.pathname,
      },
      authState: {
        authenticated: false,
      },
    });
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Server session required — Firebase user alone is not authenticated (ADR-0023)
  if (!isAuthenticated || !serverUser) {
    logger.debug('[ProtectedRoute] Not authenticated, redirecting to login:', {
      isAuthenticated,
      loading,
      path: location.pathname,
      hasUser: !!user,
      hasServerUser: !!serverUser,
    });

    const redirectDestination = isStaffProspectPath(location.pathname)
      ? staffProspectLoginHref(location.pathname, location.search)
      : '/';

    // Log routing decision with full context
    logRoutingDecision('redirect', {
      redirectDestination,
      redirectReason: 'not_authenticated',
      authState: {
        authenticated: false,
        userId: user?.uid || undefined,
        userEmail: user?.email || undefined,
        roles: serverUser?.roles || [],
      },
      additionalInfo: {
        hasUser: !!user,
        hasServerUser: !!serverUser,
        loading,
      },
    });

    return <Navigate to={redirectDestination} state={{ from: location }} replace />;
  }

  logger.debug('[ProtectedRoute] Authenticated, rendering protected content:', {
    isAuthenticated,
    path: location.pathname,
    userId: user?.uid,
    userEmail: user?.email,
    roles: serverUser?.roles || [],
  });

  // Log successful routing decision
  logRoutingDecision('allow', {
    authState: {
      authenticated: true,
      userId: user?.uid || undefined,
      userEmail: user?.email || undefined,
      roles: serverUser?.roles || [],
    },
  });

  // User is authenticated (via server session), render the protected content
  return <>{children}</>;
}

